import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Building2, Users, PieChart, ClipboardCheck, Check, Pencil, type LucideIcon } from 'lucide-react'

const STEPS = ['Welcome', 'Company Details', 'Company Structure', 'Processing Breakdown', 'Review & Submit']
const STEP_ICONS: LucideIcon[] = [Sparkles, Building2, Users, PieChart, ClipboardCheck]
// Route each step maps to, so completed steps can be clicked to jump back.
const STEP_ROUTES = ['/welcome', '/company-details', '/company-structure', '/processing-breakdown', '/review']

interface ProgressStepperProps {
  currentStep: 0 | 1 | 2 | 3 | 4
}

// Glassmorphism style shared between desktop and mobile bars
const glassStyle: React.CSSProperties = {
  background: 'var(--color-surface-glass)',
  backdropFilter: 'var(--glass-standard-blur)',
  WebkitBackdropFilter: 'var(--glass-standard-blur)',
}

// -------------------------------------------------------------------
// Step node variants
// -------------------------------------------------------------------
type NodeState = 'upcoming' | 'active' | 'complete'

function getNodeState(index: number, currentStep: number): NodeState {
  if (index < currentStep) return 'complete'
  if (index === currentStep) return 'active'
  return 'upcoming'
}

interface StepNodeProps {
  state: NodeState
  label: string
  isLast: boolean
  Icon: LucideIcon
  /** Present only on completed steps — clicking jumps back to that step. */
  onNavigate?: () => void
}

function StepNode({ state, label, isLast, Icon, onNavigate }: StepNodeProps) {
  const size = 32
  const clickable = state === 'complete' && !!onNavigate
  // When a completed step is hovered/focused, it turns into an "edit" affordance:
  // checkmark → pen, and the label takes the active brand-blue colour.
  const [editHover, setEditHover] = useState(false)
  const showEdit = clickable && editHover

  // Background for the circle
  let nodeBg: string
  if (state === 'upcoming') nodeBg = 'var(--stepper-node-upcoming)'
  else if (state === 'active') nodeBg = 'var(--stepper-node-active)'
  else nodeBg = 'var(--stepper-node-complete)'

  // Icon stroke colour — light on the saturated active/complete bgs,
  // secondary on the muted upcoming bg
  const iconColor =
    state === 'upcoming'
      ? 'var(--color-action-primary)'
      : 'var(--color-text-on-primary)'

  // Label weight
  const labelWeight = state === 'active'
    ? ('var(--weight-semibold)' as unknown as number)
    : ('var(--weight-regular)' as unknown as number)

  const labelColor = state === 'upcoming'
    ? 'var(--stepper-label-upcoming)'
    : state === 'active'
    ? 'var(--color-action-primary)'
    : 'var(--color-text-primary)'

  const wrapperStyle: React.CSSProperties = {
    minWidth: 0,
    flex: isLast ? '0 0 auto' : '1 1 0',
  }

  const inner = (
    <>
      {/* Node circle */}
      <AnimatePresence mode="wait" initial={false}>
        {state === 'active' ? (
          <motion.div
            key="active"
            className="stepper-node-active-glow"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{
              scale: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1.0] },
              opacity: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1.0] },
            }}
            style={{
              width: size,
              height: size,
              borderRadius: 'var(--radius-full)',
              background: nodeBg,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={16} color={iconColor} strokeWidth={2} aria-hidden="true" />
          </motion.div>
        ) : state === 'complete' ? (
          <motion.div
            key="complete"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.34, 1.56, 0.64, 1.0] }}
            style={{
              width: size,
              height: size,
              borderRadius: 'var(--radius-full)',
              background: nodeBg,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Completed steps show a checkmark (per Figma); on hover of a
                clickable step it becomes a pen to signal "edit / jump back".
                Both icons are stacked and cross-fade so the swap is smooth,
                not an instant flip. */}
            <span style={{ position: 'relative', width: 16, height: 16, flexShrink: 0 }}>
              {[
                { Ico: Check, sz: 16, show: !showEdit, key: 'check' },
                { Ico: Pencil, sz: 15, show: showEdit, key: 'pencil' },
              ].map(({ Ico, sz, show, key }) => (
                <Ico
                  key={key}
                  size={sz}
                  color={iconColor}
                  strokeWidth={2.5}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    opacity: show ? 1 : 0,
                    transition: 'opacity var(--duration-micro) var(--ease-out)',
                  }}
                />
              ))}
            </span>
          </motion.div>
        ) : (
          <motion.div
            key="upcoming"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.0, 0.0, 0.2, 1.0] }}
            style={{
              width: size,
              height: size,
              borderRadius: 'var(--radius-full)',
              background: nodeBg,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={16} color={iconColor} strokeWidth={2} aria-hidden="true" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step label */}
      <span
        className="stepper-node-label"
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-sm)',
          fontWeight: labelWeight,
          color: showEdit ? 'var(--color-action-primary)' : labelColor,
          transition: 'color var(--duration-micro) var(--ease-out)',
          marginTop: '6px',
          whiteSpace: 'nowrap',
          lineHeight: 'var(--leading-normal)',
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </span>
    </>
  )

  // Completed steps become real buttons so they're keyboard- and
  // screen-reader-navigable; active/upcoming steps stay plain, non-interactive.
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onNavigate}
        onMouseEnter={() => setEditHover(true)}
        onMouseLeave={() => setEditHover(false)}
        onFocus={() => setEditHover(true)}
        onBlur={() => setEditHover(false)}
        title={`Edit ${label}`}
        aria-label={`Go back and edit ${label}`}
        className="stepper-node-clickable flex flex-col items-center"
        style={{
          ...wrapperStyle,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {inner}
      </button>
    )
  }

  return (
    <div className="flex flex-col items-center" style={wrapperStyle}>
      {inner}
    </div>
  )
}

// -------------------------------------------------------------------
// Connector line between nodes
// -------------------------------------------------------------------
interface ConnectorProps {
  /** True if the step to the LEFT of this connector is complete or active */
  filled: boolean
}

function Connector({ filled }: ConnectorProps) {
  return (
    <div
      className="flex-1 relative"
      style={{
        height: 'var(--stepper-connector-height)',
        background: 'var(--stepper-connector-inactive)',
        marginBottom: 'var(--spacing-6)', // shift up to sit at node vertical center
        alignSelf: 'flex-start',
        marginTop: 'var(--spacing-4)', // (32px node / 2) = center with node
        overflow: 'hidden',
      }}
    >
      <motion.div
        initial={false}
        animate={{ scaleX: filled ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0.0, 0.2, 1.0] }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--stepper-connector-active)',
          transformOrigin: 'left center',
        }}
      />
    </div>
  )
}

// -------------------------------------------------------------------
// Main ProgressStepper
// -------------------------------------------------------------------
export function ProgressStepper({ currentStep }: ProgressStepperProps) {
  const navigate = useNavigate()
  return (
    <>
      {/* ---- DESKTOP (md+) ---- */}
      <div
        className="hidden md:flex items-start sticky"
        style={{
          ...glassStyle,
          top: '72px', // below NavBar
          zIndex: 'var(--z-sticky)' as string,
          height: '80px',
          padding: '0 var(--page-padding-horizontal)',
          borderBottom: '1px solid var(--color-outline-hairline)',
        }}
      >
        <div className="w-full flex items-start" style={{ maxWidth: 'var(--content-max-width)', margin: '0 auto', paddingTop: '14px' }}>
          {STEPS.map((label, i) => {
            const nodeState = getNodeState(i, currentStep)
            const isLast = i === STEPS.length - 1
            const connectorFilled = i <= currentStep
            const Icon = STEP_ICONS[i]

            return (
              <div key={label} className="flex items-start" style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
                <StepNode
                  state={nodeState}
                  label={label}
                  isLast={isLast}
                  Icon={Icon}
                  onNavigate={nodeState === 'complete' ? () => navigate(STEP_ROUTES[i]) : undefined}
                />
                {!isLast && <Connector filled={connectorFilled} />}
              </div>
            )
          })}
        </div>
      </div>

      {/* ---- MOBILE (below md) ---- */}
      <div
        className="flex md:hidden items-center justify-center fixed bottom-0 left-0 right-0"
        style={{
          ...glassStyle,
          height: 'var(--stepper-bar-height)',
          zIndex: 'var(--z-stepper)' as string,
          borderTop: '1px solid var(--color-outline-hairline)',
          gap: '10px',
        }}
      >
        {/* Step pill */}
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            fontWeight: 'var(--weight-semibold)' as unknown as number,
            color: 'var(--color-text-on-primary)',
            background: 'var(--stepper-node-active)',
            borderRadius: 'var(--radius-full)',
            padding: '3px 10px',
            lineHeight: 1.5,
            letterSpacing: '0.01em',
            flexShrink: 0,
          }}
        >
          {currentStep + 1}/{STEPS.length}
        </span>

        {/* Current step name */}
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-md)',
            fontWeight: 'var(--weight-semibold)' as unknown as number,
            color: 'var(--color-text-primary)',
            lineHeight: 1.5,
            letterSpacing: '0.01em',
          }}
        >
          {STEPS[currentStep]}
        </span>
      </div>
    </>
  )
}
